/**
 * When a dropped socket is worth trying again, and how long to wait.
 *
 * Pure, so the policy can be tested without a socket — the same split
 * `lobby-logic.ts` makes on the server, and for the same reason: the decisions
 * are the part worth asserting, and the plumbing around them is the part that
 * needs a runtime.
 *
 * `connection.ts` owns the retrying itself.
 */

import { CloseCode } from "@prop-hunt/shared";

/**
 * How many times to try before giving up and showing the player a dead session.
 *
 * Six, with the delays below, spans about 23 seconds. That is deliberately
 * longer than the server's CONNECTION_TIMEOUT_MS: a lobby that dropped us for
 * being silent has forgotten us by the last attempt, so the rejoin lands as a
 * clean new player rather than alongside our own ghost holding a second slot
 * of MAX_PLAYERS.
 */
export const RECONNECT_MAX_ATTEMPTS = 6;

/** First retry. Short, because much of what drops a socket is momentary. */
export const RECONNECT_BASE_DELAY_MS = 500;

/** Ceiling on the doubling. Past this the wait is worse than the outage. */
export const RECONNECT_MAX_DELAY_MS = 8_000;

/**
 * Fraction of each delay to spread the attempt over.
 *
 * A lobby is up to MAX_PLAYERS clients that all lost the same server at the
 * same instant. Without jitter they retry in lockstep, and the thundering herd
 * arrives at a Durable Object that has just come back — which is the moment it
 * is least able to take it.
 */
export const RECONNECT_JITTER = 0.25;

/**
 * Whether a close code describes something a second attempt could fix.
 *
 * The 4000–4999 range is the server having made a *decision* about this client
 * (see `CloseCode`): a stale build, a mismatched map, a full lobby. Retrying
 * one of those reconnects to the same answer, so it is only a way to fail more
 * slowly and less legibly.
 *
 * `TimedOut` is the exception, and the case this whole module exists for: the
 * server saying it stopped hearing from us is the single most retry-worthy
 * thing on the list. Everything outside the range — 1006 above all, which is
 * what a browser reports for a connection that simply vanished — is a transport
 * failure and gets tried again.
 */
export function isRetryableClose(code: number): boolean {
  if (code === CloseCode.TimedOut) return true;
  return code < 4000 || code > 4999;
}

/**
 * How long to wait before attempt `attempt` (1-based), jittered.
 *
 * Exponential rather than fixed, because the two things that drop a socket
 * want opposite delays: a momentary blip wants to be retried immediately, and
 * a server that is down wants to be left alone. Doubling serves the first
 * attempt to the first case and the later ones to the second.
 *
 * `random` is a parameter so the schedule is testable; it is `Math.random` in
 * production and there is no reason for it to be anything stronger.
 */
export function reconnectDelayMs(attempt: number, random: () => number = Math.random): number {
  const step = Math.max(1, Math.floor(attempt)) - 1;
  const base = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** step, RECONNECT_MAX_DELAY_MS);
  // Symmetric around `base`, so the expected delay is still the curve above.
  return Math.round(base * (1 + (random() * 2 - 1) * RECONNECT_JITTER));
}

/** What the one line of failure UI says while a reconnect is in flight. */
export function reconnectingDetail(attempt: number): string {
  return `connection lost — reconnecting (${attempt}/${RECONNECT_MAX_ATTEMPTS})`;
}
